// About Us — full page content
// Each key = slug. Shape: { kicker, title, tagline, intro, pillars[], stats[], faqs[], cta }

export const aboutContent = {
  "who-we-are": {
    kicker: "// ABOUT.WHO",
    title: "We are engineers who ship. Operators who deliver.",
    tagline: "A boutique operating firm with a systems-generalist edge.",
    intro:
      "TraceElements was founded on a simple thesis: modern enterprises don't need another PowerPoint deck — they need people who can architect the system, ship the code, and hold the delivery line. We are the firm we wished existed when we sat on the client side of the table.",
    pillars: [
      {
        id: "01",
        label: "ORIGIN",
        body: "Two founders — Lao Dara and Sherrie Dara — locked step between deep engineering and elite operations. The company was built to eliminate the corporate telephone game between sales, engineering, and delivery.",
        bullets: [
          { title: "Founded 2024", desc: "By practitioners, for operators." },
          { title: "Systems generalists", desc: "We build across the stack, not around it." },
          { title: "Zero middlemen", desc: "The founders are the builders." },
        ],
      },
      {
        id: "02",
        label: "BLUEPRINT",
        body: "Our model is engineered like a product. Sprints are versioned. Contracts are transparent. Every engagement is auditable end-to-end.",
        bullets: [
          { title: "Sprint-based delivery", desc: "Two-week cycles with stakeholder demos." },
          { title: "Transparent scoping", desc: "You see the estimate math." },
          { title: "Audit trail by default", desc: "Deployment logs, decision records, retros." },
        ],
      },
      {
        id: "03",
        label: "STANCE",
        body: "We turn down engagements that can't be executed with excellence. Fit matters more than fees.",
        bullets: [
          { title: "Elite over volume", desc: "We ship fewer engagements, better." },
          { title: "Craft over churn", desc: "Long-arc quality wins compounding trust." },
          { title: "Signal over noise", desc: "No sales theater, no vanity metrics." },
        ],
      },
    ],
    stats: [
      { value: "02", label: "Co-founders" },
      { value: "100%", label: "Delivery accountability" },
      { value: "0", label: "Middlemen between you and code" },
      { value: "∞", label: "Depth of stack" },
    ],
    faqs: [
      { q: "Are you a consultancy or an engineering studio?", a: "Both. We advise like a strategy firm and ship like a product team — because our founders operate in both dimensions." },
      { q: "How large are your engagements?", a: "From focused three-week diagnostics to multi-quarter platform builds. We do not do staff augmentation." },
    ],
    cta: { title: "Meet the engine.", sub: "See the co-founders behind every engagement.", href: "/the-engine" },
  },

  "leadership": {
    kicker: "// ABOUT.LEADERSHIP",
    title: "The people making the calls are the people building the systems.",
    tagline: "Our leadership doesn't manage from a deck. They deploy from the keyboard.",
    intro:
      "Leadership at TraceElements is technical and operational — never delegated. Every engagement is sponsored by a founder who is personally responsible for outcome.",
    pillars: [
      {
        id: "01",
        label: "LAO DARA · CO-FOUNDER, LEAD STACK ENGINEER",
        body: "The technical backbone. Lao architects every stack we ship — from custom AI runtimes and multi-tenant SaaS platforms to hardened enterprise cloud deployments. If it can be conceptualized, Lao ships it to scale.",
        bullets: [
          { title: "AI systems synthesis", desc: "LLM orchestration, agentic workflows, evaluation harnesses." },
          { title: "Multi-tenant cloud", desc: "Kubernetes, isolated data planes, zero-trust ingress." },
          { title: "Full-stack velocity", desc: "React, Next, FastAPI, Node — pixel to pipeline." },
        ],
      },
      {
        id: "02",
        label: "SHERRIE DARA · CO-FOUNDER, DIRECTOR OF OPERATIONS",
        body: "The execution engine. Sherrie owns every project lifecycle from scope-lock through production cutover. Nothing ships without her sign-off, and nothing slips without her escalation.",
        bullets: [
          { title: "Agile scoping", desc: "Sprint plans that survive contact with reality." },
          { title: "Stakeholder telemetry", desc: "Weekly velocity reports, real numbers, no PowerPoint." },
          { title: "Risk mitigation", desc: "Escalation ladders, contingency plans, no surprises." },
        ],
      },
    ],
    stats: [
      { value: "24h", label: "Founder response SLA" },
      { value: "100%", label: "Founder-attended kickoffs" },
      { value: "0", label: "Client-side handoffs to juniors" },
    ],
    faqs: [
      { q: "Will I always work with the founders?", a: "Yes. Every engagement is founder-sponsored end-to-end. Extended teams may support execution, but the accountable party is always Lao or Sherrie." },
    ],
    cta: { title: "Initialize your project.", sub: "Get on the founders' schedule.", href: "/terminal" },
  },

  "mission-and-values": {
    kicker: "// ABOUT.MISSION",
    title: "Engineer the future enterprise. Delete the corporate friction.",
    tagline: "Our operating principles are non-negotiable.",
    intro:
      "TraceElements exists to prove that elite engineering and rigorous project execution can coexist inside one small, accountable firm. Everything we do is filtered through six core values.",
    pillars: [
      {
        id: "01",
        label: "CRAFT",
        body: "We treat every line of code and every stakeholder update as an artifact of the firm's reputation. Craft is not a phase — it is the constant.",
        bullets: [
          { title: "Code review is non-optional", desc: "" },
          { title: "Documentation is deliverable", desc: "" },
          { title: "Design is a discipline", desc: "" },
        ],
      },
      {
        id: "02",
        label: "TRANSPARENCY",
        body: "Clients see estimates, decisions, and trade-offs in real time. No black boxes.",
        bullets: [
          { title: "Live project portal", desc: "" },
          { title: "Open sprint logs", desc: "" },
          { title: "Honest scope calls", desc: "" },
        ],
      },
      {
        id: "03",
        label: "VELOCITY",
        body: "Momentum compounds. We ship weekly, demo bi-weekly, and cutover with confidence.",
        bullets: [
          { title: "Two-week sprints", desc: "" },
          { title: "Zero missed cutovers", desc: "" },
          { title: "Async by default", desc: "" },
        ],
      },
    ],
    stats: [
      { value: "6", label: "Core values" },
      { value: "0", label: "Compromises on craft" },
    ],
    faqs: [],
    cta: { title: "See how it maps.", sub: "Explore our engagement model.", href: "/the-engine" },
  },

  "teams": {
    kicker: "// ABOUT.TEAMS",
    title: "Small pods. Deep expertise. Zero overhead.",
    tagline: "Teams are assembled per engagement — never rented out.",
    intro:
      "We do not staff bench engineers. Every TraceElements pod is hand-selected to match the mission profile of the engagement, and reports directly to a founder.",
    pillars: [
      { id: "01", label: "ENGINEERING POD", body: "Full-stack engineers, AI/ML specialists, DevOps, and QA — configured per mission.", bullets: [{ title: "Median 8+ yrs experience", desc: "" }, { title: "Cross-stack fluency", desc: "" }] },
      { id: "02", label: "OPERATIONS POD", body: "Project leads, technical PMs, and delivery analysts embedded with the engineering pod.", bullets: [{ title: "1:3 ratio of PMs to engineers", desc: "" }, { title: "Live burndown telemetry", desc: "" }] },
      { id: "03", label: "STRATEGY POD", body: "Founder-led advisory for board, exec, and cross-functional programs.", bullets: [{ title: "Zero-fluff frameworks", desc: "" }, { title: "Post-engagement continuity", desc: "" }] },
    ],
    stats: [{ value: "3", label: "Pod archetypes" }, { value: "6-12", label: "Median pod size" }],
    faqs: [],
    cta: { title: "See the roles.", sub: "We're always hiring elite operators.", href: "/careers" },
  },

  "life-at-traceelements": {
    kicker: "// LIFE.INSIDE",
    title: "Deep work. Real ownership. Async by default.",
    tagline: "The environment we architected for ourselves.",
    intro:
      "TraceElements is fully remote, deeply focused, and radically transparent. We optimize for engineers and operators who thrive on autonomy and craft.",
    pillars: [
      { id: "01", label: "FLOW.PROTOCOLS", body: "Meetings are booked in 25-minute increments. No standups. All work is documented in writing.", bullets: [{ title: "4 core hours overlap", desc: "" }, { title: "Async-first comms", desc: "" }] },
      { id: "02", label: "OWNERSHIP", body: "Every engineer owns entire vertical slices — from architecture decisions to production cutover.", bullets: [{ title: "You ship it, you name it", desc: "" }, { title: "Post-launch equity in playbooks", desc: "" }] },
      { id: "03", label: "COMPOUND.LEARNING", body: "Every retrospective feeds our internal playbook library. Institutional knowledge is a product.", bullets: [{ title: "Weekly craft sessions", desc: "" }, { title: "Annual research sprints", desc: "" }] },
    ],
    stats: [{ value: "100%", label: "Remote" }, { value: "0", label: "Weekly status meetings" }, { value: "25min", label: "Default meeting length" }],
    faqs: [],
    cta: { title: "Working environment fit?", sub: "See open roles.", href: "/careers/experienced-hires" },
  },

  "inclusion": {
    kicker: "// INCLUSION",
    title: "Elite is not a demographic. It's a discipline.",
    tagline: "We hire for craft. Period.",
    intro:
      "TraceElements is committed to building a firm where the best engineers and operators — regardless of background, geography, or credential path — can do the best work of their careers.",
    pillars: [
      { id: "01", label: "OPEN.HIRING", body: "Applications are reviewed by founders. Portfolios and take-home builds matter more than pedigree.", bullets: [{ title: "No school-name filter", desc: "" }, { title: "Blind first pass", desc: "" }] },
      { id: "02", label: "GLOBAL.BY.DESIGN", body: "We recruit worldwide and compensate to a global elite band — not a regional discount.", bullets: [{ title: "Location-agnostic pay bands", desc: "" }, { title: "Localized benefits", desc: "" }] },
      { id: "03", label: "SPONSORSHIP", body: "We invest in emerging engineers via structured mentorship pods and public playbook contributions.", bullets: [{ title: "Founder-led mentorship", desc: "" }, { title: "Conference & OSS sponsorship", desc: "" }] },
    ],
    stats: [{ value: "40+", label: "Countries represented in applicant pool" }, { value: "1", label: "Compensation band, global" }],
    faqs: [],
    cta: { title: "Apply.", sub: "The bar is craft. Nothing else.", href: "/careers/experienced-hires" },
  },

  "corporate-responsibility": {
    kicker: "// RESPONSIBILITY",
    title: "Responsible engineering is not a marketing line.",
    tagline: "Codified into every engagement we accept.",
    intro:
      "We turn down work that conflicts with our published stance on AI ethics, data stewardship, and environmental impact. Every proposal is filtered through a responsibility screen.",
    pillars: [
      { id: "01", label: "AI.ETHICS", body: "We publish an ethics scorecard for every AI engagement we accept — including model risk, data provenance, and human-in-the-loop protocols.", bullets: [{ title: "Model risk documentation", desc: "" }, { title: "Bias audits shipped", desc: "" }] },
      { id: "02", label: "DATA.STEWARDSHIP", body: "Client data never touches unvetted third parties. Retention is engineered by default.", bullets: [{ title: "Zero third-party leakage", desc: "" }, { title: "Cryptographic audit logs", desc: "" }] },
      { id: "03", label: "CLIMATE", body: "We measure the carbon footprint of every platform we ship and offset annual firm emissions.", bullets: [{ title: "Green cloud region defaults", desc: "" }, { title: "Annual public report", desc: "" }] },
    ],
    stats: [{ value: "100%", label: "AI engagements scored" }, { value: "0", label: "Data brokers touched" }],
    faqs: [],
    cta: { title: "Read the annual report.", sub: "Full transparency, published yearly.", href: "/insights" },
  },

  "corporate-initiatives": {
    kicker: "// INITIATIVES",
    title: "The programs we fund with our own capital.",
    tagline: "Not marketing. Investment.",
    intro:
      "TraceElements reinvests a percentage of every engagement into structured initiatives that push the firm and the industry forward.",
    pillars: [
      { id: "01", label: "OPEN.PLAYBOOKS", body: "We open-source select frameworks, sprint templates, and engineering runbooks.", bullets: [{ title: "12+ playbooks published", desc: "" }, { title: "MIT licensed", desc: "" }] },
      { id: "02", label: "RESEARCH.SPRINTS", body: "Quarterly self-funded research sprints on emerging tech — AI evals, quantum, edge compute.", bullets: [{ title: "Quarterly cadence", desc: "" }, { title: "Public results", desc: "" }] },
      { id: "03", label: "GRANT.PROGRAM", body: "Micro-grants to under-resourced founders building infrastructure-critical open source.", bullets: [{ title: "$50k annual pool", desc: "" }, { title: "Nomination-based", desc: "" }] },
    ],
    stats: [{ value: "12+", label: "Playbooks published" }, { value: "4/yr", label: "Research sprints" }],
    faqs: [],
    cta: { title: "See the playbooks.", sub: "Free, forever.", href: "/insights" },
  },

  "accolades": {
    kicker: "// ACCOLADES",
    title: "The signal we've earned.",
    tagline: "Selected recognition from clients, industry, and peer engineers.",
    intro:
      "We don't chase awards. We track the moments where clients and peers have publicly recognized the standard of work we ship.",
    pillars: [
      { id: "01", label: "CLIENT.SIGNAL", body: "Every engagement ends with a public case study. Referral rate is our most trusted metric.", bullets: [{ title: "88% referral rate", desc: "" }, { title: "12+ published case studies", desc: "" }] },
      { id: "02", label: "INDUSTRY.RECOGNITION", body: "Featured in engineering publications, invited to speak at technical conferences.", bullets: [{ title: "Featured: The New Stack", desc: "" }, { title: "Speakers: AI Engineer Summit", desc: "" }] },
      { id: "03", label: "PEER.TRUST", body: "Open-source contributors, industry advisory boards, and technical review panels.", bullets: [{ title: "OSS maintainer standing", desc: "" }, { title: "3 advisory board seats", desc: "" }] },
    ],
    stats: [{ value: "88%", label: "Client referral rate" }, { value: "12+", label: "Case studies" }, { value: "3", label: "Advisory board seats" }],
    faqs: [],
    cta: { title: "Read the studies.", sub: "Real engagements, real numbers.", href: "/insights" },
  },

  "alumni-network": {
    kicker: "// ALUMNI",
    title: "Once inside the engine, always in the network.",
    tagline: "TraceElements alumni operate at the top of their fields.",
    intro:
      "Former TraceElements engineers and operators go on to lead engineering at high-growth startups, join enterprise architecture teams, or start their own firms. We stay connected.",
    pillars: [
      { id: "01", label: "NETWORK.ACCESS", body: "Private alumni Slack, quarterly virtual roundtables, annual in-person off-site.", bullets: [{ title: "Private community", desc: "" }, { title: "Peer intros", desc: "" }] },
      { id: "02", label: "CONTINUING.CRAFT", body: "Access to internal playbooks, research previews, and tech deep-dives — for life.", bullets: [{ title: "Lifetime playbook access", desc: "" }, { title: "Early tech briefings", desc: "" }] },
      { id: "03", label: "REFERRAL.LOOP", body: "Alumni-referred hires and clients receive first review.", bullets: [{ title: "Priority intake", desc: "" }, { title: "Alumni bounty program", desc: "" }] },
    ],
    stats: [{ value: "Lifetime", label: "Playbook access" }],
    faqs: [],
    cta: { title: "Join or reconnect.", sub: "For current + former team.", href: "/terminal" },
  },

  "locations": {
    kicker: "// LOCATIONS",
    title: "Remote-first. Globally distributed. Locally accountable.",
    tagline: "We follow the talent — not zip codes.",
    intro:
      "TraceElements is a fully remote firm with core operations distributed across North America, Europe, and Asia-Pacific. We staff engagements based on time-zone fit, never real estate.",
    pillars: [
      { id: "01", label: "AMERICAS", body: "Engineering and operations coverage across US, Canada, and Latin America.", bullets: [{ title: "Primary HQ: New York (registered)", desc: "" }, { title: "Distributed pods across EST/PST", desc: "" }] },
      { id: "02", label: "EMEA", body: "European engineering and delivery pods with GDPR-native expertise.", bullets: [{ title: "Talent hubs: London, Berlin, Lisbon", desc: "" }] },
      { id: "03", label: "APAC", body: "Coverage for follow-the-sun engagements and APAC-market delivery.", bullets: [{ title: "Talent hubs: Singapore, Tokyo, Sydney", desc: "" }] },
    ],
    stats: [{ value: "3", label: "Regions" }, { value: "15+", label: "Talent hubs" }, { value: "24h", label: "Follow-the-sun coverage" }],
    faqs: [],
    cta: { title: "See open roles.", sub: "Anywhere, provided you ship.", href: "/careers" },
  },

  "newsroom": {
    kicker: "// NEWSROOM",
    title: "What TraceElements is shipping, saying, and signing.",
    tagline: "Real announcements. Real dates.",
    intro:
      "Follow TraceElements' engagement launches, product releases, hires, and industry commentary.",
    pillars: [
      { id: "01", label: "PRESS.RELEASES", body: "Official company announcements — engagement wins, product launches, executive additions.", bullets: [] },
      { id: "02", label: "MEDIA.COVERAGE", body: "Third-party coverage of the firm and our founders.", bullets: [] },
      { id: "03", label: "SIGNALS", body: "Short-form founder posts on industry moves we're tracking.", bullets: [] },
    ],
    stats: [],
    faqs: [{ q: "How do I submit a media inquiry?", a: "Route via /terminal with subject line 'PRESS' — we respond within 24 hours." }],
    cta: { title: "Read insights.", sub: "Long-form perspectives.", href: "/insights" },
  },

  "webinars": {
    kicker: "// WEBINARS",
    title: "Live sessions. No dead pixels.",
    tagline: "Founders and senior engineers — unscripted.",
    intro:
      "TraceElements runs a monthly technical webinar series covering AI systems architecture, cybersecurity hardening, regulatory shifts, and engineering leadership.",
    pillars: [
      { id: "01", label: "MONTHLY.CADENCE", body: "First Thursday of every month, 45 minutes, live Q&A.", bullets: [{ title: "45 min sessions", desc: "" }, { title: "Recording available", desc: "" }] },
      { id: "02", label: "TECHNICAL.DEPTH", body: "No introductory content — every session assumes practitioner-level familiarity.", bullets: [{ title: "Live code walkthroughs", desc: "" }, { title: "Architecture teardowns", desc: "" }] },
      { id: "03", label: "OPEN.REPLAYS", body: "All past sessions are permanently free on our channel.", bullets: [{ title: "Full archive", desc: "" }] },
    ],
    stats: [{ value: "12/yr", label: "Live sessions" }, { value: "45min", label: "Session length" }],
    faqs: [],
    cta: { title: "Register.", sub: "Next session announced monthly.", href: "/terminal" },
  },
};
